import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { createCourse, deleteCourse, getCourses, updateCourse } from '../../services/admin/courseService';
import { CourseCreationForm } from '../../components/admin/courses/CourseCreationForm';
import { BackButton } from '../../components/common/BackButton';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { CategoryManager } from '../../components/admin/courses/CategoryManager';
import { CourseNotFound } from '../../components/admin/courses/CourseNotFound';
import { CourseCard } from '../../components/admin/courses/CourseCard';
import { useAuth } from '../../hooks/auth/useAuth';
import type { Course } from '../../types/course';

export function CourseManagement() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, sessionChecked } = useAuth();

  useEffect(() => {
    // Only redirect after session check is complete
    if (sessionChecked && !isAuthenticated) {
      navigate('/login');
      return;
    }

    // Redirect non-admin users
    if (sessionChecked && !isAdmin) {
      navigate('/dashboard');
      return;
    }

    // Load courses if user is admin
    const loadCourses = async () => {
      try {
        // Load courses if admin
        const coursesData = await getCourses();
        setCourses(coursesData);
      } catch (error) {
        console.error('Error loading courses:', error);
        if (!isNetworkError(error)) {
          toast.error('Failed to load courses');
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated && isAdmin) {
      loadCourses();
    }
  }, [isAuthenticated, isAdmin, sessionChecked, navigate]);

  const handleDeleteCourse = async (courseId: string) => {
    if (!window.confirm('Are you sure you want to delete this course?')) {
      return;
    }
    
    try {
      // Optimistically update UI
      setCourses(prev => prev.filter(course => course.id !== courseId));
      
      await deleteCourse(courseId);
      toast.success('Course deleted successfully');
    } catch (error) {
      console.error('Error deleting course:', error);
      toast.error('Failed to delete course');
      // Restore courses on error
      const coursesData = await getCourses();
      setCourses(coursesData);
    }
  };

  const handleDeleteAllCourses = async () => {
    if (!window.confirm('Are you sure you want to delete ALL courses? This action cannot be undone.')) {
      return;
    }
    
    setIsDeletingAll(true);
    try {
      await deleteAllCourses();
      // Reload courses after deletion
      const coursesData = await getCourses();
      setCourses(coursesData);
    } catch (error) {
      console.error('Error deleting all courses:', error);
    } finally {
      setIsDeletingAll(false);
    }
  };

  const handleCreateCourse = async (courseData: Partial<Course>) => {
    setIsLoading(true);
    try {
      const result = editingCourse 
        ? await updateCourse(editingCourse.id, courseData)
        : await createCourse(courseData);

      if (result) {
        if (editingCourse) {
          setCourses(prev => prev.map(course => 
            course.id === result.id ? result : course
          ));
          toast.success('Course updated successfully');
        } else {
          setCourses(prev => [...prev, result]);
          toast.success('Course created successfully');
        }
        setEditingCourse(null);
        setIsFormOpen(false);
      }
    } catch (error) {
      console.error('Error creating course:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error(editingCourse ? 'Failed to update course' : 'Failed to create course');
        throw error;
      }
      setIsLoading(false);
    }
  };

  const handleEditCourse = (course: Course) => {
    setEditingCourse(course);
    setIsFormOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background-dark py-8">
      <div className="max-w-7xl mx-auto px-4">
        <BackButton to="/admin" label="Back to Admin Dashboard" />
        
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-black">Course Management</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleDeleteAllCourses}
              disabled={isDeletingAll}
              className="px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center disabled:opacity-50"
            >
              {isDeletingAll ? 'Deleting...' : 'Delete All Courses'}
            </button>
            <button
              onClick={() => setIsFormOpen(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Course
            </button>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search courses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-black placeholder-gray-400 focus:ring-2 focus:ring-accent-green focus:border-accent-green"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          {/* Category Management Section */}
          <div className="p-6 border-b border-gray-200">
            <CategoryManager />
          </div>

          {/* Course List Section */}
          {filteredCourses.length === 0 ? (
            <CourseNotFound onAddCourse={() => setIsFormOpen(true)} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {filteredCourses.map((course) => (
                <CourseCard 
                  key={course.id} 
                  course={course} 
                  onDelete={handleDeleteCourse}
                  onEdit={handleEditCourse}
                />
              ))}
            </div>
          )}
        </div>

        {/* Course Creation Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-bold text-black">
                  {editingCourse ? 'Edit Course' : 'Add New Course'}
                </h2>
              </div>
              <div className="p-6">
                <CourseCreationForm
                  onCancel={() => {
                    setIsFormOpen(false);
                    setEditingCourse(null);
                  }}
                  onSubmit={handleCreateCourse}
                  initialData={editingCourse}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}